import React from "react";

import {
  ArrowRight,
  MessageCircle,
  CheckCircle2,
} from "lucide-react";

import { WHATSAPP_URL, PHONE } from "@/lib/brand";

import HeroDashboard from "@/assets/hero-dashboard.png";

export default function Hero() {
  return (
    <section
      id="top"
      className="relative overflow-hidden bg-gradient-to-b from-white via-slate-50 to-white"
    >
      {/* Background Effects */}
      <div className="absolute inset-0 gf-dot-bg opacity-60 pointer-events-none" />

      <div className="absolute -top-32 -right-24 h-[420px] w-[420px] rounded-full bg-[var(--gf-blue-soft)] blur-3xl opacity-70 pointer-events-none" />

      <div className="absolute -bottom-24 -left-24 h-[320px] w-[320px] rounded-full bg-[var(--gf-mint)] blur-3xl opacity-70 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-14 lg:pt-24 pb-16 lg:pb-28 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">

        {/* LEFT CONTENT */}
        <div className="lg:col-span-7 gf-fade-up">

          {/* Badge */}
          <div
            data-testid="launch-offer-badge"
            className="inline-flex items-center gap-2 bg-white border border-[var(--gf-border)] gf-shadow-soft px-4 py-2 rounded-full text-xs font-semibold tracking-wide"
          >
            <span className="text-[var(--gf-green)] text-base leading-none">
              ●
            </span>

            <span className="text-[var(--gf-primary)]">
              Free Demo Website Available
            </span>

            <span className="text-[var(--gf-text-2)] hidden sm:inline">
              — Limited Time
            </span>
          </div>

          {/* Heading */}
          <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-[68px] font-extrabold leading-[1.02] tracking-tight">
            Grow Your Business With{" "}
            <span className="text-[var(--gf-blue)]">
              Stunning Websites
            </span>{" "}
            & Smart Marketing
          </h1>

          {/* Description */}
          <p className="mt-6 text-lg text-[var(--gf-text-2)] max-w-2xl leading-relaxed">
            We help clinics, gyms, labs, restaurants & local businesses get
            more customers through high-converting websites, SEO, Google Ads &
            WhatsApp automation.
          </p>

          {/* Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">

            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-btn"
              className="inline-flex justify-center items-center gap-2.5 bg-[var(--gf-green)] hover:bg-[var(--gf-green-deep)] text-white font-semibold h-14 px-7 rounded-full text-base gf-shadow-soft transition-all hover:-translate-y-0.5"
            >
              <MessageCircle className="h-5 w-5" />

              WhatsApp Now

              <span className="opacity-80 font-normal text-sm hidden sm:inline">
                {PHONE}
              </span>
            </a>

            <a
              href="#lead-form"
              data-testid="hero-demo-btn"
              className="inline-flex justify-center items-center gap-2 bg-[var(--gf-primary)] hover:bg-slate-800 text-white font-semibold h-14 px-7 rounded-full text-base transition-all hover:-translate-y-0.5"
            >
              Get Free Consultation

              <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          {/* Features */}
          <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3 text-sm text-[var(--gf-text-2)]">

            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-[var(--gf-green)]" />
              SEO Optimized
            </span>

            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-[var(--gf-green)]" />
              Mobile-first design
            </span>

            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-[var(--gf-green)]" />
              Click-to-call & WhatsApp
            </span>
          </div>

          {/* Social Proof */}
          <div className="mt-8 flex items-center gap-4">

            <div className="flex -space-x-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-10 w-10 rounded-full border-2 border-white bg-[var(--gf-blue-soft)]"
                />
              ))}
            </div>

            <div>
              <p className="text-sm font-semibold">
                Trusted by Local Businesses
              </p>

              <p className="text-xs text-[var(--gf-text-2)]">
                Websites designed for real customer growth
              </p>
            </div>
          </div>
        </div>

        {/* RIGHT IMAGE */}
        <div
          className="lg:col-span-5 relative gf-fade-up overflow-visible"
          style={{ animationDelay: "120ms" }}
        >
          <img
            src={HeroDashboard}
            alt="GrowFlow Dashboard"
            className="w-[135%] max-w-none h-auto object-cover gf-float lg:-ml-16"
          />
        </div>
      </div>
    </section>
  );
}
